import React from 'react';
import { useTranslation } from 'react-i18next';

interface LoadingSpinnerProps {
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  text?: string;
  showText?: boolean;
  className?: string;
}

/**
 * Consistent, always-visible loading spinner. Uses inline keyframes so the
 * animation never gets stuck and stays visible in light/dark themes.
 *
 * Sizes:
 * - xs: 16px (icon buttons, inline)
 * - sm: 24px (small buttons, compact cards)
 * - md: 36px (standard cards, modals)
 * - lg: 48px (page-level, large cards)
 * - xl: 64px (full-page loading)
 */
const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  size = 'md',
  text,
  showText = true,
  className = '',
}) => {
  const { t } = useTranslation();
  const displayText = text ?? t('common.loading');
  const sizeClasses = {
    xs: 'w-4 h-4 min-w-[1rem] min-h-[1rem]',
    sm: 'w-6 h-6 min-w-[1.5rem] min-h-[1.5rem]',
    md: 'w-9 h-9 min-w-[2.25rem] min-h-[2.25rem]',
    lg: 'w-12 h-12 min-w-[3rem] min-h-[3rem]',
    xl: 'w-16 h-16 min-w-[4rem] min-h-[4rem]',
  };
  const textSizes = {
    xs: 't-tag',
    sm: 't-tag',
    md: 't-body',
    lg: 't-body',
    xl: 't-card-title',
  };
  const gapClasses = { xs: 'gap-2', sm: 'gap-2', md: 'gap-3', lg: 'gap-3', xl: 'gap-4' };
  const shouldShowText = showText && (size === 'md' || size === 'lg' || size === 'xl');

  const ringMinHeight = size === 'xl' ? 'min-h-[4rem]' : size === 'lg' ? 'min-h-[3rem]' : '';

  return (
    <div className={`flex flex-col items-center justify-center ${gapClasses[size]} min-h-0 ${ringMinHeight} ${className}`} role="status" aria-busy="true" aria-label={t('common.loading')}>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className={`${sizeClasses[size]} shrink-0 animate-spin`}
        viewBox="0 0 256 256"
        aria-hidden="true"
        style={{ fill: 'var(--ds-brand)' }}
      >
        <path d="M128 63.04c-5.104 0-9.28-4.176-9.28-9.28V16.64c0-5.104 4.176-9.28 9.28-9.28s9.28 4.176 9.28 9.28v37.12c0 5.104-4.176 9.28-9.28 9.28zm52.548 21.692c-2.32 0-4.756-.928-6.612-2.668-3.596-3.596-3.596-9.512 0-13.108l26.216-26.216c3.596-3.596 9.512-3.596 13.108 0s3.596 9.512 0 13.108l-26.216 26.216c-1.856 1.856-4.176 2.668-6.496 2.668zm58.812 52.548h-37.12c-5.104 0-9.28-4.176-9.28-9.28s4.176-9.28 9.28-9.28h37.12c5.104 0 9.28 4.176 9.28 9.28s-4.176 9.28-9.28 9.28zm-32.596 78.764c-2.32 0-4.756-.928-6.612-2.668l-26.216-26.216c-3.596-3.596-3.596-9.512 0-13.108s9.512-3.596 13.108 0l26.216 26.216c3.596 3.596 3.596 9.512 0 13.108-1.74 1.74-4.176 2.668-6.496 2.668zM128 248.64c-5.104 0-9.28-4.176-9.28-9.28v-37.12c0-5.104 4.176-9.28 9.28-9.28s9.28 4.176 9.28 9.28v37.12c0 5.104-4.176 9.28-9.28 9.28zm-78.764-32.596c-2.32 0-4.756-.928-6.612-2.668-3.596-3.596-3.596-9.512 0-13.108l26.216-26.216c3.596-3.596 9.512-3.596 13.108 0s3.596 9.512 0 13.108l-26.216 26.216c-1.74 1.74-4.06 2.668-6.496 2.668zm4.524-78.764H16.64c-5.104 0-9.28-4.176-9.28-9.28s4.176-9.28 9.28-9.28h37.12c5.104 0 9.28 4.176 9.28 9.28s-4.176 9.28-9.28 9.28zm21.692-52.548c-2.32 0-4.756-.928-6.612-2.668l-26.1-26.216c-3.596-3.596-3.596-9.512 0-13.108s9.512-3.596 13.108 0l26.216 26.216c3.596 3.596 3.596 9.512 0 13.108-1.856 1.856-4.176 2.668-6.612 2.668z" />
      </svg>
      {shouldShowText && displayText && (
        <p className={`${textSizes[size]} t-muted font-medium`}>{displayText}</p>
      )}
      <span className="sr-only">{displayText}</span>
    </div>
  );
};

export default LoadingSpinner;
